import React, { useState, useRef, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import { downloadReferralPDF } from '../../../utils/pdfGenerator';

// Map technical keys to French labels
const REFERRAL_REASON_LABELS = {
  'pulpite_irreversible': 'Pulpite irréversible',
  'necrose_pulpaire': 'Nécrose pulpaire',
  'reprise_traitement': 'Reprise de traitement',
  'lesion_periapicale': 'Lésion péri-apicale',
  'felure_fracture': 'Fêlure / fracture suspectée',
  'diagnostic_douleur': 'Diagnostic de douleur',
  'autre': 'Autre'
};

const getReferralReasonLabel = (key) => {
  return REFERRAL_REASON_LABELS?.[key] || key;
};

const PDFViewer = ({ pdfData, practitionerData, onEdit, onPrint, onDownload }) => {
  const [scale, setScale] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const viewerRef = useRef(null);
  const totalPages = 1;

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 2));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setScale(1);
  };

  const handlePrint = () => {
    window.print();
    if (onPrint) onPrint();
  };

  const handleDownload = () => {
    // Generate filename from patient data
    const firstName = pdfData?.patientInfo?.firstName || '';
    const lastName = pdfData?.patientInfo?.lastName || '';
    const patientInitials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`?.toUpperCase() || 'Patient';
    const referenceNumber = `REF-${Date.now()?.toString()?.slice(-8)}`;
    
    // Generate and download PDF with practitioner data
    downloadReferralPDF(pdfData, patientInitials, referenceNumber, practitionerData);
    
    if (onDownload) onDownload();
  };

  // Compute patient initials from firstName and lastName
  const getPatientInitials = () => {
    const firstName = pdfData?.patientInfo?.firstName || '';
    const lastName = pdfData?.patientInfo?.lastName || '';
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`?.toUpperCase() || 'Non spécifié';
  };

  // Compute practitioner initials from logo_initials or full_name
  const getPractitionerInitials = () => {
    if (practitionerData?.logoInitials) {
      return practitionerData?.logoInitials?.toUpperCase();
    }
    const fullName = practitionerData?.practitionerName || '';
    const nameParts = fullName?.trim()?.split(' ');
    if (nameParts?.length >= 2) {
      return `${nameParts?.[0]?.[0] || ''}${nameParts?.[nameParts?.length - 1]?.[0] || ''}`?.toUpperCase();
    }
    return 'DR';
  };

  // Get practitioner full name with title
  const getPractitionerFullName = () => {
    const title = practitionerData?.practitionerTitle || 'Dr';
    const name = practitionerData?.practitionerName || 'Praticien';
    return `${title} ${name}`;
  };

  // Get practitioner full address
  const getPractitionerAddress = () => {
    const parts = [];
    if (practitionerData?.address) parts?.push(practitionerData?.address);
    if (practitionerData?.postalCode) parts?.push(practitionerData?.postalCode);
    if (practitionerData?.city) parts?.push(practitionerData?.city);
    return parts?.length > 0 ? parts?.join(', ') : 'Adresse non spécifiée';
  };

  // Get logo dimensions based on size setting
  const getLogoDimensions = () => {
    const logoSize = practitionerData?.logoSize || 'medium';
    // Convert mm to pixels (1mm ≈ 3.78px at 96 DPI) to match pdfGenerator.js dimensions
    // pdfGenerator uses: small=10mm, medium=15mm, large=18mm, xlarge=36mm
    switch (logoSize) {
      case 'small':
        return { width: '38px', height: '38px', fontSize: '14px' }; // 10mm * 3.78
      case 'medium':
        return { width: '57px', height: '57px', fontSize: '18px' }; // 15mm * 3.78
      case 'large':
        return { width: '68px', height: '68px', fontSize: '22px' }; // 18mm * 3.78
      case 'xlarge':
        return { width: '136px', height: '136px', fontSize: '26px' }; // 36mm * 3.78
      default:
        return { width: '57px', height: '57px', fontSize: '18px' };
    }
  };

  // Get header minimum height based on logo size to prevent overlap
  const getHeaderMinHeight = () => {
    const logoSize = practitionerData?.logoSize || 'medium';
    switch (logoSize) {
      case 'small':
        return '50px';
      case 'medium':
        return '70px';
      case 'large':
        return '85px';
      case 'xlarge':
        return '155px'; // Extra space for xlarge logo
      default:
        return '70px';
    }
  };

  // Get logo alignment style based on position setting
  const getLogoAlignment = () => {
    const logoPosition = practitionerData?.logoPosition || 'left';
    switch (logoPosition) {
      case 'left':
        return { justifyContent: 'flex-start' };
      case 'center':
        return { justifyContent: 'center' };
      case 'right':
        return { justifyContent: 'flex-end' };
      default:
        return { justifyContent: 'flex-start' };
    }
  };

  // Get logo position style (absolute positioning like PDF)
  const getLogoPositionStyle = () => {
    const logoPosition = practitionerData?.logoPosition || 'left';
    const dimensions = getLogoDimensions();
    
    switch (logoPosition) {
      case 'left':
        return { position: 'absolute', left: '0' };
      case 'center':
        return { position: 'absolute', left: '50%', transform: 'translateX(-50%)' };
      case 'right':
        return { position: 'absolute', right: '0' };
      default:
        return { position: 'absolute', left: '0' };
    }
  };

  // Get practitioner name position based on logo position
  const getNamePositionStyle = () => {
    const logoPosition = practitionerData?.logoPosition || 'left';
    const dimensions = getLogoDimensions();
    
    switch (logoPosition) {
      case 'center':
        return { marginLeft: '0' }; // Name stays at left when logo is centered
      case 'right':
        return { marginLeft: '0' }; // Name stays at left when logo is right
      case 'left':
      default:
        return { marginLeft: `calc(${dimensions?.width} + 12px)` }; // Name offset by logo width + spacing
    }
  };

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-viewer-toolbar">
        <div className="toolbar-left">
          <span className="page-indicator caption">
            Page {currentPage} sur {totalPages}
          </span>
        </div>

        <div className="toolbar-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            iconName="ZoomOut"
          >
            Zoom -
          </Button>
          <span className="zoom-level caption">{Math.round(scale * 100)}%</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={scale >= 2}
            iconName="ZoomIn"
          >
            Zoom +
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetZoom}
            iconName="Maximize2"
          >
            Réinitialiser
          </Button>
        </div>

        <div className="toolbar-right">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            iconName="Printer"
            iconPosition="left"
          >
            Imprimer
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            iconName="Download"
            iconPosition="left"
          >
            Télécharger
          </Button>
        </div>
      </div>
      <div className="pdf-viewer-content" ref={viewerRef}>
        <div 
          className="pdf-document-wrapper"
          style={{ transform: `scale(${scale})` }}
        >
          <div className="pdf-document" style={{ maxWidth: '210mm', margin: '0 auto', padding: '15mm', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            {/* COMPACT HEADER WITH LOGO - Positioned higher to match PDF */}
            <div style={{ marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #e5e7eb', position: 'relative', minHeight: getHeaderMinHeight() }}>
              {/* Logo positioned independently */}
              <div style={{ ...getLogoPositionStyle(), top: '0' }}>
                {practitionerData?.logoUrl ? (
                  <img 
                    src={practitionerData?.logoUrl} 
                    alt="Logo du cabinet"
                    style={{ 
                      width: getLogoDimensions()?.width, 
                      height: getLogoDimensions()?.height, 
                      objectFit: 'contain',
                      borderRadius: '6px'
                    }}
                    onError={(e) => {
                      // Fallback to initials if logo fails to load
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{ 
                    width: getLogoDimensions()?.width, 
                    height: getLogoDimensions()?.height, 
                    backgroundColor: '#3b82f6', 
                    borderRadius: '6px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <span style={{ color: '#fff', fontSize: getLogoDimensions()?.fontSize, fontWeight: 'bold' }}>{getPractitionerInitials()}</span>
                  </div>
                )}
              </div>
              
              {/* Practitioner name positioned based on logo placement */}
              <div style={{ ...getNamePositionStyle() }}>
                <h1 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{getPractitionerFullName()}</h1>
                <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Endodontie exclusive</p>
              </div>
              
              <div style={{ textAlign: 'right', marginTop: '4px' }}>
                <p style={{ fontSize: '9px', color: '#6b7280', margin: 0 }}>
                  Généré le {new Date()?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* COMPACT SECTION COMPONENT */}
            {(() => {
              const CompactSection = ({ title, children }) => (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ backgroundColor: '#f3f4f6', padding: '6px 8px', marginBottom: '8px', borderRadius: '4px' }}>
                    <h2 style={{ fontSize: '11px', fontWeight: '600', color: '#1f2937', margin: 0 }}>{title}</h2>
                  </div>
                  <div style={{ padding: '0 8px' }}>
                    {children}
                  </div>
                </div>
              );

              const CompactField = ({ label, value, fullWidth = false }) => (
                <div style={{ marginBottom: '8px', width: fullWidth ? '100%' : '48%', display: 'inline-block', verticalAlign: 'top', paddingRight: fullWidth ? '0' : '2%' }}>
                  <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: '600', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '10px', color: '#1f2937' }}>{value || 'Non spécifié'}</div>
                </div>
              );

              const patientFullName = `${pdfData?.patientInfo?.firstName?.trim() || ''} ${pdfData?.patientInfo?.lastName?.trim() || ''}`?.trim() || 'Non spécifié';
              const sexLabel = pdfData?.patientInfo?.sex === 'male' ? 'Homme' : 
                               pdfData?.patientInfo?.sex === 'female' ? 'Femme' : 
                               pdfData?.patientInfo?.sex === 'other' ? 'Autre' : 'Non spécifié';
              const teethText = (pdfData?.toothNumbers && pdfData?.toothNumbers?.length > 0) 
                ? pdfData?.toothNumbers?.join(', ') 
                : 'Aucune dent spécifiée';
              const reasonsText = (pdfData?.referralReasons && pdfData?.referralReasons?.length > 0)
                ? pdfData?.referralReasons?.map(r => getReferralReasonLabel(r))?.join(', ')
                : 'Aucun motif spécifié';

              return (
                <>
                  {/* REFERRING DENTIST */}
                  <CompactSection title="Confrère adresseur">
                    <CompactField label="Nom / Prénom" value={pdfData?.referringDentist?.fullName} />
                    <CompactField label="Cabinet" value={pdfData?.referringDentist?.practiceName} />
                    <CompactField label="Email" value={pdfData?.referringDentist?.email} fullWidth />
                    <CompactField label="Téléphone" value={pdfData?.referringDentist?.phone} />
                  </CompactSection>
                  {/* PATIENT */}
                  <CompactSection title="Patient">
                    <CompactField label="Nom et Prénom" value={patientFullName} fullWidth />
                    <CompactField label="Année de naissance" value={String(pdfData?.patientInfo?.yearOfBirth || 'Non spécifié')} />
                    <CompactField label="Sexe" value={sexLabel} />
                    {pdfData?.patientInfo?.phone?.trim() && (
                      <CompactField label="Téléphone" value={pdfData?.patientInfo?.phone?.trim()} />
                    )}
                    {pdfData?.patientInfo?.email?.trim() && (
                      <CompactField label="Email" value={pdfData?.patientInfo?.email?.trim()} />
                    )}
                  </CompactSection>
                  {/* CLINICAL INFO */}
                  <CompactSection title="Informations cliniques">
                    <CompactField label="Dent(s) concernée(s) (FDI)" value={teethText} fullWidth />
                    <CompactField label="Motif(s) d'adressage" value={reasonsText} fullWidth />
                  </CompactSection>
                  {/* CLINICAL NOTES */}
                  {pdfData?.clinicalNotes?.trim() && (
                    <CompactSection title="Notes cliniques">
                      <CompactField label="Observations" value={pdfData?.clinicalNotes?.trim()} fullWidth />
                    </CompactSection>
                  )}
                  {/* IMAGING */}
                  {(pdfData?.imaging?.downloadLink || pdfData?.imaging?.password || pdfData?.imaging?.imagingCenter) && (
                    <CompactSection title="Imagerie / CBCT">
                      {pdfData?.imaging?.downloadLink && <CompactField label="Lien de téléchargement" value={pdfData?.imaging?.downloadLink} fullWidth />}
                      {pdfData?.imaging?.password && <CompactField label="Mot de passe" value={pdfData?.imaging?.password} />}
                      {pdfData?.imaging?.imagingCenter && <CompactField label="Centre d'imagerie" value={pdfData?.imaging?.imagingCenter} />}
                    </CompactSection>
                  )}
                  {/* UPLOADED FILES */}
                  {pdfData?.uploadedFiles && pdfData?.uploadedFiles?.length > 0 && (
                    <CompactSection title="Pièces jointes">
                      <div style={{ fontSize: '9px', color: '#1f2937' }}>
                        {pdfData?.uploadedFiles?.map((file, idx) => (
                          <div key={idx} style={{ marginBottom: '4px' }}>
                            • {file?.name} ({(file?.size / 1024)?.toFixed(1)} Ko)
                          </div>
                        ))}
                      </div>
                    </CompactSection>
                  )}
                  {/* IMAGES PREVIEW */}
                  {pdfData?.uploadedFiles?.filter(f => f?.type?.startsWith('image/'))?.length > 0 && (
                    <div style={{ marginTop: '16px', pageBreakBefore: 'always' }}>
                      <div style={{ backgroundColor: '#f3f4f6', padding: '6px 8px', marginBottom: '12px', borderRadius: '4px' }}>
                        <h2 style={{ fontSize: '11px', fontWeight: '600', color: '#1f2937', margin: 0 }}>Images jointes</h2>
                      </div>
                      {pdfData?.uploadedFiles?.filter(f => f?.type?.startsWith('image/'))?.map((file, idx) => (
                        <div key={idx} style={{ marginBottom: '12px' }}>
                          <p style={{ fontSize: '9px', color: '#6b7280', marginBottom: '4px', fontStyle: 'italic' }}>{file?.name}</p>
                          <div style={{ border: '1px solid #e5e7eb', padding: '4px', borderRadius: '4px' }}>
                            <img 
                              src={file?.preview || file?.base64} 
                              alt={file?.name}
                              style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain' }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* SIGNATURE SECTION */}
            {practitionerData?.signatureUrl && (
              <div style={{ marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '9px', color: '#6b7280', fontStyle: 'italic', marginBottom: '8px' }}>
                  Signature du praticien
                </div>
                <img 
                  src={practitionerData?.signatureUrl} 
                  alt="Signature du praticien"
                  style={{ 
                    width: '150px', 
                    height: 'auto', 
                    maxHeight: '60px',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* COMPACT FOOTER */}
            <div style={{ marginTop: '20px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '9px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                Cabinet d'endodontie – {getPractitionerFullName()}
              </div>
              <div style={{ fontSize: '8px', color: '#6b7280', marginBottom: '2px' }}>
                {getPractitionerAddress()} • {practitionerData?.phone || 'Téléphone non spécifié'} • {practitionerData?.email || 'Email non spécifié'}
              </div>
              <div style={{ fontSize: '7px', color: '#d1d5db' }}>
                Conformité RGPD : Données traitées côté client uniquement.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;